import clsx from 'clsx';
import dot from 'dot-object';
import { isEqual, orderBy } from 'lodash';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../button/button';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { addPositionToOptions, forceAssert } from '../../common-functions';
import AddNewModal from '../../custom-modals/add-new-modal/add-new-modal.component';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon from '../../odin-icon/odin-icon.component';
import type { CheckboxChangeEventType } from '../checkbox/checkbox.component';
import Checkbox from '../checkbox/checkbox.component';
import type { DropDownNoSelection, DropDownResult, OptionWithOptionalProperties } from '../drop-down/drop-down-types';
import ListBox from '../list-box/list-box.component';
import type { OptionBuilderState } from './option-builder.reducer';
import reducer, { OptionBuilderActionType } from './option-builder.reducer';

export interface OptionBuilderProps
  extends DynamicFieldComponentProps<Array<OptionWithOptionalProperties>>,
    DynamicFieldDataProps {
  allowSearch?: boolean;
  hideFooter?: boolean;
  removeAllowed?: boolean;
}

const OptionBuilder = ({
  id,
  value = [],
  label,
  readOnly = false,
  required,
  error,
  data,
  onChange,
  textField = 'text',
  valueField = 'value',
  allowSearch = true,
  hideFooter = false,
  removeAllowed = false,
}: OptionBuilderProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState(value ?? []);
  const [selectedOption, setSelectedOption] = useState<OptionWithOptionalProperties[]>();

  const editClicked = useRef(false);
  const optionValueRef = useRef(value ?? []);
  const selectedOptionRef = useRef<OptionWithOptionalProperties[] | undefined>(undefined);
  const defaultRef = useRef(false);
  const archivedRef = useRef(false);

  const initialValues: OptionBuilderState = {
    isArchivedChecked: false,
    isDefaultChecked: false,
    showAddNewModal: false,
  };

  const [state, dispatch] = useReducer(reducer, initialValues);

  const onCloseAddOptionModal = () => {
    editClicked.current = false;
    dispatch({
      type: OptionBuilderActionType.resetAllValues,
    });
  };

  const onChangeSelected = (values?: Array<DropDownResult> | DropDownNoSelection) => {
    if (Array.isArray(values)) {
      const builderOptions: Array<OptionWithOptionalProperties> = values.map((item) => {
        const { text, value, originalData } = item;
        return {
          text,
          value,
          originalData,
          archivedOption: originalData?.archivedOption as boolean,
          defaultOption: originalData?.defaultOption as boolean,
          position: originalData?.position as number,
        };
      });

      setSelectedOption(builderOptions);
    }
  };

  const onDefaultCheckboxChange = (value?: CheckboxChangeEventType) => {
    const isChecked = value ? value.checked : false;
    defaultRef.current = isChecked;
    dispatch({
      type: OptionBuilderActionType.updateDefaultProperty,
      defaultOption: isChecked,
    });
  };

  const onArchivedCheckboxChange = (value?: CheckboxChangeEventType) => {
    const isChecked = value ? value.checked : false;
    archivedRef.current = isChecked;
    dispatch({
      type: OptionBuilderActionType.updateArchivedProperty,
      defaultOption: isChecked,
    });
  };

  const moveOptionUp = () => {
    if (selectedOption) {
      const position = selectedOption[0].position;
      if (position && position > 0) {
        const newOptions = [...options];
        const temp = newOptions[position - 1];
        newOptions[position - 1] = newOptions[position];
        newOptions[position] = temp;
        const orderedOptions = addPositionToOptions(newOptions);
        setOptions(orderedOptions);
        setSelectedOption([orderedOptions[position - 1]]);
      }
    }
  };

  const moveOptionDown = () => {
    if (selectedOption) {
      const position = selectedOption[0].position;
      if (position === 0 || (position && position < options.length - 1)) {
        const newOptions = [...options];
        const temp = newOptions[position + 1];
        newOptions[position + 1] = newOptions[position];
        newOptions[position] = temp;
        const orderedOptions = addPositionToOptions(newOptions);
        setOptions(orderedOptions);
        setSelectedOption([orderedOptions[position + 1]]);
      }
    }
  };

  const sortData = () => {
    const sortedOptions = orderBy(options, ['text'], ['asc']);
    setOptions(addPositionToOptions(sortedOptions));
  };

  const editOption = () => {
    if (selectedOption) {
      editClicked.current = true;
      selectedOptionRef.current = selectedOption;
      dispatch({
        type: OptionBuilderActionType.editOption,
        archivedOption: selectedOption[0].archivedOption ?? false,
        defaultOption: selectedOption[0].defaultOption ?? false,
      });
    }
  };

  const removeOption = () => {
    if (selectedOption) {
      const selectedValue = selectedOption[0].value;
      const filteredOptions = options.filter((option) => option.value !== selectedValue);
      const orderedOptions = addPositionToOptions(filteredOptions);
      setOptions(orderedOptions);
      setSelectedOption(undefined);
    }
  };

  const onNewItemCreated = (value: JsonDataItem) => {
    const createdValue: OptionWithOptionalProperties = {
      value: dot.pick('value', value),
      text: dot.pick('text', value),
      originalData: value,
      archivedOption: archivedRef.current,
      defaultOption: defaultRef.current,
    };
    if (editClicked.current && selectedOptionRef.current) {
      const valueToEdit = selectedOptionRef.current[0].value as string;
      const editOptionList = optionValueRef.current.filter((option) => option.value !== valueToEdit);
      // assign originalData and id if editing existing option
      createdValue.originalData = {
        id: selectedOptionRef.current[0].originalData?.id,
        value: selectedOptionRef.current[0].value,
        text: selectedOptionRef.current[0].text,
        archivedOption: selectedOptionRef.current[0].archivedOption,
        defaultOption: selectedOptionRef.current[0].defaultOption,
      };
      createdValue.value = selectedOptionRef.current[0].value;
      selectedOptionRef.current = [createdValue];
      setOptions([...addPositionToOptions(editOptionList), { ...createdValue, position: editOptionList.length }]);
      setSelectedOption([createdValue]);
    } else {
      setOptions(addPositionToOptions([...optionValueRef.current, createdValue]));
      setSelectedOption([createdValue]);
    }
  };

  const openAddNewModal = () => {
    defaultRef.current = false;
    archivedRef.current = false;
    dispatch({
      type: OptionBuilderActionType.openAddNew,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: notify parent when internal options change, not when onChange changes
  useEffect(() => {
    if (!isEqual(optionValueRef.current, options)) {
      optionValueRef.current = options;
      onChange?.(options);
    }
  }, [options]);

  // Sync options with value prop when it changes externally
  useEffect(() => {
    if (!isEqual(value, optionValueRef.current)) {
      optionValueRef.current = value ?? [];
      setOptions(value ?? []);
    }
  }, [value]);

  const footer = (
    <>
      <Checkbox
        name="default"
        label={t('default')}
        onChange={onDefaultCheckboxChange}
        initialChecked={state.isDefaultChecked}
      />
      <Checkbox
        name="archived"
        label={t('archived')}
        onChange={onArchivedCheckboxChange}
        initialChecked={state.isArchivedChecked}
      />
    </>
  );

  const readOnlyElement = useMemo(
    () =>
      value?.length > 0 ? (
        <ul className="list-none text-sm">
          {value?.map((item) => (
            <li key={item.text}>
              {item.text}
              {item.archivedOption && ` [${t('archived')}]`}
              {item.defaultOption && ` [${t('default')}]`}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyFieldLabel />
      ),

    [t, value]
  );

  return (
    <>
      <AddNewModal
        fieldId={id}
        data={data}
        formId="Add new option"
        header={t('add-new-item')}
        textField="text"
        valueField="value"
        placeholder={t('add-new-option')}
        footer={hideFooter ? undefined : footer}
        visible={state.showAddNewModal}
        onClose={onCloseAddOptionModal}
        buttonText={t('ok')}
        buttonType="submit"
        defaultValue={selectedOption && editClicked.current ? selectedOption[0].text : ''}
        onItemCreated={onNewItemCreated}
      />
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && readOnlyElement}
      {!readOnly && (
        <div className={clsx('flex flex-row w-full', { 'border border-error': error })}>
          <ListBox
            id={id}
            value={selectedOption}
            textField={textField}
            valueField={valueField}
            data={forceAssert<JsonData>(options)}
            onChange={onChangeSelected}
            allowSearch={allowSearch}
            preventMultiSelect
          />
          <div className="flex flex-col justify-center items-center mx-2 m-1">
            <Button type="text" onClick={openAddNewModal}>
              {t('add-new')}
            </Button>
            <Button type="text" onClick={moveOptionUp} disabled={!selectedOption || selectedOption[0].position === 0}>
              <OdinIcon icon="ChevronUp" />
            </Button>
            <Button
              type="text"
              onClick={moveOptionDown}
              disabled={!selectedOption || selectedOption[0].position === options.length - 1}
            >
              <OdinIcon icon="ChevronDown" />
            </Button>
            <Button type="text" onClick={sortData}>
              {t('sort')}
            </Button>
            <Button type="text" onClick={editOption} disabled={!selectedOption}>
              {t('edit')}
            </Button>
            {removeAllowed && (
              <Button type="text" onClick={removeOption} disabled={!selectedOption}>
                {t('remove')}
              </Button>
            )}
          </div>
        </div>
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
};
export default OptionBuilder;
